import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Rng } from '../../../core/rng';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { beamSolid, boxSolid, planPoint } from '../solids';
import { readNumber } from './params';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf } from './planFrame';
import type { PlanRect } from './genericBlock';

export const TENTER = {
  /** The painting's frame rows repeat every 13.2-13.9 m once inverted through the painter view. */
  rowSpacing: 13.5,
  /** Room to walk round the frames inside the fence. */
  margin: 4,
  bay: 3,
  postHalf: 0.07,
  railHalf: 0.05,
  /** tenters.org: finished woollen cloth 1.3-1.8 m wide hangs between a low and a high rail. */
  bottomRail: 0.5,
  topRail: 2.3,
  clothHalf: 0.015,
  /** Keeps each cloth a separate closed solid from the posts and rails it hangs between. */
  clothGap: 0.02,
  /** Red is 3.4% of the painted frame area (a* above 12) against near-black cloth. */
  redShare: 0.034,
  fence: { inset: 0.3, height: 1.2, bay: 3, postHalf: 0.06, railHalf: 0.04 },
} as const;

/** One frame in the field's plan: across the field at u, from v0 to v1. */
export interface TenterRow {
  readonly u: number;
  readonly v0: number;
  readonly v1: number;
}

export interface TenterColors {
  readonly timber: LinearRGB;
  readonly cloth: LinearRGB;
  readonly dyed: LinearRGB;
}

interface TenterStyles {
  readonly timber: VertexStyle;
  readonly cloth: VertexStyle;
  readonly dyed: VertexStyle;
}

function fieldPlan(landmark: Landmark): { frame: Frame; rect: PlanRect } {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  return { frame: meshFrameOf(plan), rect: planBounds(plan) };
}

/** Frames across the field's length, `rowSpacing` apart and centred between the fence ends. */
export function tenterRows(landmark: Landmark, warn: (msg: string) => void): TenterRow[] {
  const { rect } = fieldPlan(landmark);
  const spacing = readNumber(landmark.params, 'rowSpacing', TENTER.rowSpacing, warn);
  const count = Math.floor((rect.u1 - rect.u0 - 2 * TENTER.margin) / spacing) + 1;
  const first = (rect.u0 + rect.u1) / 2 - ((count - 1) * spacing) / 2;
  return Array.from({ length: count }, (_, i) => ({
    u: first + i * spacing,
    v0: rect.v0 + TENTER.margin,
    v1: rect.v1 - TENTER.margin,
  }));
}

function stations(from: number, to: number, bay: number): number[] {
  const bays = Math.ceil((to - from) / bay);
  return Array.from({ length: bays + 1 }, (_, k) => from + ((to - from) * k) / bays);
}

function buildFrame(builder: MeshBuilder, styles: TenterStyles, frame: Frame, row: TenterRow, rng: Rng): void {
  const t = TENTER;
  const posts = stations(row.v0, row.v1, t.bay);
  for (const v of posts) {
    beamSolid(builder, styles.timber, frame, planPoint(row.u, 0, v), planPoint(row.u, t.topRail + t.railHalf, v), t.postHalf);
  }
  for (const y of [t.bottomRail, t.topRail]) {
    beamSolid(builder, styles.timber, frame, planPoint(row.u, y, row.v0), planPoint(row.u, y, row.v1), t.railHalf);
  }
  const low = t.bottomRail + t.railHalf + t.clothGap;
  const high = t.topRail - t.railHalf - t.clothGap;
  posts.slice(1).forEach((v, k) => {
    const style = rng.chance(t.redShare) ? styles.dyed : styles.cloth;
    const from = posts[k]! + t.postHalf + t.clothGap;
    const to = v - t.postHalf - t.clothGap;
    const min = planPoint(row.u - t.clothHalf, low, to);
    const max = planPoint(row.u + t.clothHalf, high, from);
    boxSolid(builder, style, frame, min, max, { top: true, bottom: true });
  });
}

/** Posts and one rail round the field, just inside its footprint. */
function buildFence(builder: MeshBuilder, style: VertexStyle, frame: Frame, rect: PlanRect): void {
  const f = TENTER.fence;
  const [u0, u1, v0, v1] = [rect.u0 + f.inset, rect.u1 - f.inset, rect.v0 + f.inset, rect.v1 - f.inset];
  const loop: [number, number][] = [
    [u0, v0],
    [u1, v0],
    [u1, v1],
    [u0, v1],
  ];
  const railY = f.height - f.railHalf;
  loop.forEach(([ua, va], i) => {
    const [ub, vb] = loop[(i + 1) % loop.length]!;
    const along = Math.hypot(ub - ua, vb - va);
    for (const s of stations(0, along, f.bay).slice(0, -1)) {
      const k = s / along;
      const [u, v] = [ua + (ub - ua) * k, va + (vb - va) * k];
      beamSolid(builder, style, frame, planPoint(u, 0, v), planPoint(u, f.height, v), f.postHalf);
    }
    beamSolid(builder, style, frame, planPoint(ua, railY, va), planPoint(ub, railY, vb), f.railHalf);
  });
}

/**
 * Lakenramen: rows of frames with dyed woollen cloth stretched between two rails, in a fenced
 * field on the Amstel ('die Ramen' on the 1544 woodcut; de Fremery p.16).
 */
export function buildTenterField(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: TenterColors,
  ids: { readonly edgeId: number; readonly seed: number },
  rng: Rng,
  warn: (msg: string) => void
): void {
  const { frame, rect } = fieldPlan(landmark);
  const base = { ...ids, strokeAngle: 0, extent: [TENTER.bay, TENTER.topRail] as const };
  const styles: TenterStyles = {
    timber: { ...base, color: colors.timber, surface: SURFACE.wood },
    cloth: { ...base, color: colors.cloth, surface: SURFACE.cloth },
    dyed: { ...base, color: colors.dyed, surface: SURFACE.cloth },
  };
  for (const row of tenterRows(landmark, warn)) buildFrame(builder, styles, frame, row, rng);
  buildFence(builder, styles.timber, frame, rect);
}
